// per docs/shape-uc26.md §Addendum (no-Grafana build) and §Addendum — wireframe
// alignment, §UC26-01/02, §UC-DASH-04/07/08
// Three readings of analytics.dashboard_config.role_scope, tabbed: the
// dashboard-first list (this file), the role-first editor
// (docs/ui-shape-role-first-dashboards.md) and the observer role definitions.
// Gestiune tablouri (wireframes/react/src/pages/AdminTablouri.tsx): the list in
// the order observers see it, with the visualisations, the roles it is shared
// with, the refresh and the time interval, the version, and reorder /
// activate / versions with restore / export / import. The screen also absorbed
// the Roluri observator menu entry — the second tab is where an observer role is
// defined and bound to the dashboards it may see (UC61-02/03).
// The owner ruled on 2026-09-01 (spec 002 §"Owner rulings, 2026-09-01" #5) that this screen
// also EDITS: Grafana is embedded here with its authoring chrome, beside the role sharing the
// table already carries, so the administrator draws the dashboard and decides who gets it in one
// place. The sharing cannot follow it into Grafana — analytics.dashboard_config.role_scope is the
// only source the security gate reads and Grafana OSS has no notion of a SIMSM role — which is
// exactly why the editor came here instead. "Tablouri de bord" is the reading half of that split
// and carries no editing at all.
// Backed by /api/v1/admin/dashboards.
import { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import type { TableColumn, TableRowData } from '@egov-moldova/mud/loader';
import {
  MudButton,
  MudFileInput,
  MudIcon,
  MudModal,
  MudToast,
  MudSelect,
  MudTab,
  MudTable,
  MudTabs,
  MudTag,
  MudInfoBox,
} from '@egov-moldova/mud-react';
import { apiClient } from '../../app/api-client';
import {
  type ActiveFilter,
  EmptyRegisterMessage,
  EmptyState,
  FilterBar,
  formatDateTime,
  onModalDismiss,
  onRowOpen,
  PageHeader,
  RowActionsMenu,
} from '@simsm/ui-shared';
import { QueryState } from '../../components/QueryState/QueryState';
import { useConfirm } from '../../components/ConfirmDialog/ConfirmDialog';
import { useObserverRoles, useRoleLabel } from '../../hooks/use-observer-roles';
import { resolveDashboardReach } from '../../hooks/use-dashboard-reach';
import { useGrafanaDashboards } from '../../hooks/use-grafana-dashboards';
import { ObserverRolesPanel } from './ObserverRolesPage';
import { RoleDashboardsPanel } from './RoleDashboardsPage';
import {
  grafanaEditorSrc,
  type GrafanaEditorTarget,
} from '../../features/dashboards/grafana-embed';
import './dashboards-admin.css';

interface DashboardView {
  id: string;
  slug: string;
  title: string;
  description: string;
  roleScope: string[];
  grafanaUid: string;
  refreshIntervalSeconds: number;
  defaultRange: string;
  maxRange: string;
  maxLookback: string;
  sortOrder: number;
  active: boolean;
  version: number;
  // When the version now in force was published. The list used to print
  // `updatedAtIso`, which one reorder click rewrote on every dashboard, so all
  // twelve rows claimed the same publication instant (audit D2).
  publishedAtIso: string;
}

// UC-DASH-08: `warnings` names the items imported although this Grafana already
// held a different dashboard at their uid, which was kept
// (docs/shape-ops-dashboard-definitions-versioned.md §2.2).
interface ImportReport {
  imported: number;
  errors: { slug: string; code: string }[];
  warnings?: { slug: string; code: string }[];
}

interface DashboardVersionRow {
  version: number;
  snapshotJson: string;
  createdBy: string;
  createdAtIso: string;
}

const EXPORT_ALL_URL = '/api/v1/admin/dashboards/export';
const SECTIONS = ['dashboards', 'by-role', 'observer-roles'] as const;
type Section = (typeof SECTIONS)[number];

function normalize(value: string): string {
  return value.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

export interface DashboardFilter {
  query: string;
  status: string;
  role: string;
}

// The wireframe searches title, description and the roles the dashboard is
// shared with, diacritics-insensitively, and filters status and role apart.
export function matchesDashboardFilter(
  row: Pick<DashboardView, 'title' | 'description' | 'slug' | 'roleScope' | 'active'>,
  filter: DashboardFilter,
  roleLabel: (roleCode: string) => string,
): boolean {
  if (filter.status === 'active' && !row.active) return false;
  if (filter.status === 'inactive' && row.active) return false;
  if (filter.role && !row.roleScope.includes(filter.role)) return false;
  if (!filter.query) return true;
  const haystack = normalize(
    [row.title, row.description, row.slug, ...row.roleScope.map(roleLabel)].join(' '),
  );
  return haystack.includes(normalize(filter.query));
}

// The formats the field takes; MUD writes the caption naming them in the page language.
const DASHBOARD_IMPORT_ACCEPT = '.json';

export function DashboardsAdminPage(): JSX.Element {
  const { t } = useTranslate();
  const roleLabel = useRoleLabel();
  const definedRoles = useObserverRoles();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [versionsFor, setVersionsFor] = useState<DashboardView | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<number | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<ImportReport | null>(null);
  // What the embedded Grafana editor is showing. It opens on Grafana's own dashboard list and a
  // row's action points it at that row's dashboard, so the sharing above and the panels below
  // are the same subject.
  const [editorTarget, setEditorTarget] = useState<GrafanaEditorTarget>({ kind: 'browse' });
  const editorRef = useRef<HTMLElement | null>(null);

  const sectionParam = searchParams.get('section') ?? '';
  const section: Section = SECTIONS.includes(sectionParam as Section)
    ? (sectionParam as Section)
    : 'dashboards';
  const query = searchParams.get('q') ?? '';
  const statusFilter = searchParams.get('status') ?? '';
  const roleFilter = searchParams.get('role') ?? '';

  function setParams(patch: Record<string, string>): void {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setSearchParams(next, { replace: true });
  }

  const dashboards = useQuery({
    queryKey: ['admin-dashboards'],
    queryFn: () =>
      apiClient
        .get<{ dashboards: DashboardView[] }>('/admin/dashboards')
        .then((r) => r.data.dashboards),
  });
  const rows = dashboards.data ?? [];
  const activeCount = rows.filter((row) => row.active).length;
  const catalogue = useGrafanaDashboards();
  const roleCodes = (definedRoles.data ?? []).map((role) => role.roleCode);

  const hasFilters = Boolean(query || statusFilter || roleFilter);
  const filtered = rows.filter((row) =>
    matchesDashboardFilter(row, { query, status: statusFilter, role: roleFilter }, roleLabel),
  );

  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey: ['admin-dashboards'] });
    queryClient.invalidateQueries({ queryKey: ['dashboards-list'] });
  };

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      apiClient.patch(`/admin/dashboards/${id}`, { active }),
    onSuccess: invalidate,
  });

  const reorder = useMutation({
    mutationFn: (ids: string[]) => apiClient.post('/admin/dashboards/reorder', { ids }),
    onSuccess: invalidate,
  });

  const versionsQuery = useQuery({
    queryKey: ['admin-dashboard-versions', versionsFor?.id],
    queryFn: () =>
      apiClient
        .get<{ versions: DashboardVersionRow[] }>(`/admin/dashboards/${versionsFor?.id}/versions`)
        .then((r) => r.data.versions),
    enabled: Boolean(versionsFor),
  });

  const restore = useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) =>
      apiClient.post(`/admin/dashboards/${id}/restore/${version}`, {}),
    onSuccess: () => {
      invalidate();
      setRestoreTarget(null);
      setVersionsFor(null);
    },
  });

  const doImport = useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData();
      body.append('file', file);
      const { data } = await apiClient.post<ImportReport>('/admin/dashboards/import', body);
      return data;
    },
    onSuccess: (result) => {
      invalidate();
      setImportResult(result);
      setImportFile(null);
    },
    onError: () => setErrorMessage(t('dashboards_admin.error.import_failed')),
  });

  // Reorder writes the whole order, so it only runs on the unfiltered list; the
  // buttons are disabled while a filter is on and the indices then line up.
  function moveDashboard(index: number, delta: -1 | 1): void {
    const target = index + delta;
    if (target < 0 || target >= rows.length) return;
    const ids = rows.map((row) => row.id);
    const tmp = ids[index]!;
    ids[index] = ids[target]!;
    ids[target] = tmp;
    reorder.mutate(ids);
  }

  // The search term gets no chip: it clears from the box's own ×, which is the
  // wireframe's own rule for the shared bar (BaraFiltre.tsx:35).
  const activeFilters: ActiveFilter[] = [
    ...(statusFilter
      ? [
          {
            key: 'status',
            label: t('dashboards_admin.filter.status'),
            value: t(`dashboards_admin.status.${statusFilter}`),
            onRemove: () => setParams({ status: '' }),
          },
        ]
      : []),
    ...(roleFilter
      ? [
          {
            key: 'role',
            label: t('dashboards_admin.filter.role'),
            value: roleLabel(roleFilter),
            onRemove: () => setParams({ role: '' }),
          },
        ]
      : []),
  ];

  // UC-DASH-07. The stored value is a second count clamped to 30..3600, not a
  // vocabulary, so the label is derived rather than looked up.
  function refreshLabel(seconds: number): string {
    if (seconds % 3600 === 0) {
      const hours = seconds / 3600;
      return t(
        hours === 1 ? 'dashboards_admin.refresh.hour_one' : 'dashboards_admin.refresh.hour_other',
        { count: hours },
      );
    }
    if (seconds % 60 === 0) {
      return t('dashboards_admin.refresh.minute_other', { count: seconds / 60 });
    }
    return t('dashboards_admin.refresh.second_other', { count: seconds });
  }

  // The Grafana dashboard this row embeds, named the way the administrator
  // knows it. A uid with no catalogue entry is left as the uid rather than
  // rendered as blank: that row is a broken embed and has to look like one.
  function sourceTitle(uid: string): string {
    return (catalogue.data ?? []).find((row) => row.uid === uid)?.title ?? uid;
  }

  // The editor is on the same screen, so a row's "open in Grafana" loads it here rather than in
  // a second tab the administrator then has to keep in step with the sharing.
  function openInEditor(uid: string): void {
    setEditorTarget({ kind: 'dashboard', uid });
    editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // The wireframe's `tbl zebra compact` (AdminTablouri.tsx:95-96) as AGE's own
  // table: six columns, the row controls trailing the data as the wireframe
  // draws them. Every cell is slotted, so mud-table's nowrap `.cell-text` never
  // ellipsises a title, and mud-table's own wrapper is the one horizontal
  // scroll owner (docs/ui-shape.md §Shared leading row-actions column).
  const columns: TableColumn[] = [
    { key: 'dashboard', label: t('dashboards_admin.column.dashboard') },
    { key: 'source', label: t('dashboards_admin.column.source') },
    { key: 'roles', label: t('dashboards_admin.column.roles') },
    { key: 'refresh', label: t('dashboards_admin.column.refresh') },
    { key: 'version', label: t('dashboards_admin.column.version') },
    { key: 'actions', label: '' },
  ];
  const tableRows: TableRowData[] = filtered.map((d) => ({ id: d.id }));

  // The wireframe's versions table (AdminTablouri.tsx:195): version, when, by
  // whom, and the restore control trailing. SIMSM keeps no change note per
  // version, so that column is absent rather than empty.
  const versionColumns: TableColumn[] = [
    { key: 'version', label: t('dashboards_admin.versions.column.version'), width: '96px' },
    { key: 'when', label: t('dashboards_admin.versions.column.when') },
    { key: 'by', label: t('dashboards_admin.versions.column.by') },
    { key: 'actions', label: '' },
  ];
  const versionRows = versionsQuery.data ?? [];
  const versionTableRows: TableRowData[] = versionRows.map((row) => ({ id: row.version }));

  return (
    <section className="simsm-page simsm-dashboards-admin">
      <PageHeader
        title={t('dashboards_admin.title')}
        lede={t('dashboards_admin.lede')}
        actions={
          section === 'dashboards' ? (
            <>
              <MudButton
                shape="circular"
                variant="neutral"
                size="md"
                onClick={() => window.open(EXPORT_ALL_URL, '_blank')}
              >
                <MudIcon slot="icon-start" name="download" size={16} />
                {t('dashboards_admin.action.export_all')}
              </MudButton>
              <MudButton
                shape="circular"
                variant="neutral"
                size="md"
                onClick={() => {
                  setImportResult(null);
                  setImportFile(null);
                  setShowImport(true);
                }}
              >
                <MudIcon slot="icon-start" name="upload" size={16} />
                {t('dashboards_admin.action.import')}
              </MudButton>
              <MudButton
                shape="circular"
                variant="primary"
                size="md"
                onClick={() => navigate('/system-admin/dashboards-admin/new')}
              >
                <MudIcon slot="icon-start" name="plus-large" size={16} />
                {t('dashboards_admin.action.create')}
              </MudButton>
            </>
          ) : undefined
        }
      />

      <nav
        className="simsm-dashboards-admin__tabs"
        aria-label={t('dashboards_admin.section.aria_label')}
      >
        <MudTabs
          size="sm"
          value={section}
          aria-label={t('dashboards_admin.section.aria_label')}
          onMudChange={(e: CustomEvent<{ value: string }>) =>
            setParams({ section: e.detail.value === 'dashboards' ? '' : e.detail.value })
          }
        >
          <MudTab value="dashboards" label={t('dashboards_admin.section.dashboards')} />
          <MudTab value="by-role" label={t('dashboards_admin.section.by_role')} />
          <MudTab value="observer-roles" label={t('dashboards_admin.section.observer_roles')} />
        </MudTabs>
      </nav>

      {section === 'by-role' ? (
        <RoleDashboardsPanel />
      ) : section === 'observer-roles' ? (
        <ObserverRolesPanel />
      ) : (
        <>
          {errorMessage ? (
            <MudInfoBox emphasis="strong" role="alert" variant="error">
              {errorMessage}
            </MudInfoBox>
          ) : null}

          <FilterBar
            search={{
              value: query,
              onChange: (value) => setParams({ q: value }),
              placeholder: t('dashboards_admin.search.placeholder'),
              ariaLabel: t('dashboards_admin.search.aria'),
              clearLabel: t('common.filter.clear_search'),
            }}
            active={activeFilters}
            onReset={() => setParams({ q: '', status: '', role: '' })}
            labels={{
              filters: t('common.filter.button'),
              reset: t('common.filter.reset'),
              panelAriaLabel: t('dashboards_admin.filter.aria_label'),
              activeAriaLabel: t('common.filter.active_aria_label'),
            }}
            info={
              <span className="simsm-dashboards-admin__filter-summary">
                {t('dashboards_admin.summary', { active: activeCount, total: rows.length })}
                {hasFilters ? (
                  <>
                    <br />
                    {t('dashboards_admin.reorder_needs_no_filter')}
                  </>
                ) : null}
              </span>
            }
            panel={
              <>
                <label className="simsm-filter-bar__field">
                  <span className="simsm-filter-bar__field-label">
                    {t('dashboards_admin.filter.status')}
                  </span>
                  <MudSelect
                    size="medium"
                    aria-label={t('dashboards_admin.filter.status')}
                    value={statusFilter}
                    placeholder={t('dashboards_admin.filter.status_any')}
                    onMudChange={(e: CustomEvent<{ value: string }>) =>
                      setParams({ status: e.detail.value })
                    }
                  >
                    <option value="">{t('dashboards_admin.filter.status_any')}</option>
                    <option value="active">{t('dashboards_admin.status.active')}</option>
                    <option value="inactive">{t('dashboards_admin.status.inactive')}</option>
                  </MudSelect>
                </label>
                <label className="simsm-filter-bar__field">
                  <span className="simsm-filter-bar__field-label">
                    {t('dashboards_admin.filter.role')}
                  </span>
                  <MudSelect
                    size="medium"
                    aria-label={t('dashboards_admin.filter.role')}
                    value={roleFilter}
                    placeholder={t('dashboards_admin.filter.role_any')}
                    onMudChange={(e: CustomEvent<{ value: string }>) =>
                      setParams({ role: e.detail.value })
                    }
                  >
                    <option value="">{t('dashboards_admin.filter.role_any')}</option>
                    {(definedRoles.data ?? []).map((role) => (
                      <option key={role.roleCode} value={role.roleCode}>
                        {roleLabel(role.roleCode)}
                      </option>
                    ))}
                  </MudSelect>
                </label>
              </>
            }
          />

          <QueryState
            query={dashboards}
            /* Two absences, and the register used to answer both by vanishing.
               The condition was `filtered.length === 0` — the same shape spec 009
               found on the RSUD diff — so a search matching nothing replaced the
               whole register with a box. The sentence did not lie, because the
               copy branched on `hasFilters`, but the reader lost the column
               headings, which are the only thing on screen naming what was being
               filtered. The wireframe refuses this four separate times
               (Nefinalizate.tsx:60, AdminLocatii.tsx:220, AdminRegistratori.tsx:494,
               Rapoarte.tsx:107): the card replaces the register only when the
               register has never had a row; a filter that excludes everything
               leaves the table standing with one centred line naming the total.
               So this branch is now the whole collection. */
            isEmpty={rows.length === 0}
            empty={
              <EmptyState
                icon="chart"
                title={t('dashboards_admin.empty.title')}
                body={t('dashboards_admin.empty.body')}
              />
            }
          >
            <div className="simsm-dashboards-admin__table">
              <MudTable
                className="simsm-table"
                columns={columns}
                rows={tableRows}
                rowStyle="zebra"
                disableSort={true}
                aria-label={t('dashboards_admin.title')}
                // The whole row opens the editor, the row's one visible action.
                hoverable
                onMudRowClick={onRowOpen((index) => {
                  const d = filtered[index];
                  if (d) navigate(`/system-admin/dashboards-admin/${d.slug}/edit`);
                })}
              >
                <span slot="header-cell-actions" className="visually-hidden">
                  {t('dashboards_admin.column.actions')}
                </span>
                {/* The longest header in the register, and the only one wider
                    than the values under it. Slotted so it may run to two lines
                    the way the wireframe's `.tbl.compact th` does — the comment
                    there says why in as many words: so the table fits with the
                    sidebar expanded. */}
                <span slot="header-cell-refresh" className="simsm-table__th">
                  {t('dashboards_admin.column.refresh')}
                </span>
                {/* The second absence. `tableRows` is already `filtered`, so the
                    table draws its head, its frame and one `empty-row` spanning
                    every column, and this is what goes in it: the count the
                    register holds when the filters come off. */}
                <EmptyRegisterMessage>
                  {t('dashboards_admin.filter.empty', { total: rows.length })}
                </EmptyRegisterMessage>
                {filtered.map((d, rowIndex) => {
                  const index = rows.indexOf(d);
                  const uid = d.grafanaUid;
                  const inherited = resolveDashboardReach(d.roleScope, roleCodes).filter(
                    (row) => row.kind === 'inherited',
                  );
                  return [
                    <span
                      key={`dashboard-${d.id}`}
                      slot={`cell-dashboard-${rowIndex}`}
                      className="simsm-dashboards-admin__cell simsm-dashboards-admin__cell--stack simsm-dashboards-admin__cell--name"
                    >
                      <span className="simsm-dashboards-admin__cell-title">
                        <strong>{d.title}</strong>
                        {d.active ? null : (
                          <MudTag
                            variant="status"
                            semantic="neutral"
                            label={t('dashboards_admin.status.inactive')}
                          />
                        )}
                      </span>
                      <span className="simsm-dashboards-admin__cell-meta">
                        <span className="simsm-dashboards-admin__cell-meta-key">
                          {t('dashboards_admin.meta.route')}
                        </span>{' '}
                        <code className="simsm-dashboards-admin__cell-slug">
                          /dashboards/{d.slug}
                        </code>
                      </span>
                    </span>,
                    <span
                      key={`source-${d.id}`}
                      slot={`cell-source-${rowIndex}`}
                      className="simsm-dashboards-admin__cell simsm-dashboards-admin__cell--stack simsm-dashboards-admin__cell--source"
                    >
                      <span className="simsm-dashboards-admin__cell-title">
                        {uid ? (
                          <strong>{sourceTitle(uid)}</strong>
                        ) : (
                          <MudTag
                            variant="status"
                            semantic="danger"
                            label={t('dashboards_admin.source.missing')}
                          />
                        )}
                      </span>
                      {uid ? (
                        <span className="simsm-dashboards-admin__cell-meta">
                          <span className="simsm-dashboards-admin__cell-meta-key">
                            {t('dashboards_admin.meta.grafana_uid')}
                          </span>{' '}
                          <code className="simsm-dashboards-admin__cell-slug">{uid}</code>
                        </span>
                      ) : null}
                    </span>,
                    <span
                      key={`roles-${d.id}`}
                      slot={`cell-roles-${rowIndex}`}
                      className="simsm-dashboards-admin__cell-tags simsm-dashboards-admin__cell--roles"
                    >
                      {d.roleScope.length > 0 ? (
                        d.roleScope.map((roleCode) => (
                          <span key={roleCode} className="simsm-dashboards-admin__cell-tag">
                            <MudTag variant="info" label={roleLabel(roleCode)} />
                          </span>
                        ))
                      ) : (
                        <span className="simsm-dashboards-admin__cell-muted">
                          {t('dashboards_admin.not_shared')}
                        </span>
                      )}
                      {/* D5: a Specific Observer is an Observer, so a grant to
                          OBSERVER already carries roles nobody ticked. Without
                          this line the administrator grants them again. */}
                      {inherited.length > 0 ? (
                        <span className="simsm-dashboards-admin__cell-meta">
                          {t('dashboards_admin.reach.also_reaches', {
                            roles: inherited.map((row) => roleLabel(row.roleCode)).join(', '),
                          })}
                        </span>
                      ) : null}
                    </span>,
                    <span
                      key={`refresh-${d.id}`}
                      slot={`cell-refresh-${rowIndex}`}
                      className="simsm-dashboards-admin__cell simsm-dashboards-admin__cell--stack simsm-dashboards-admin__cell--refresh"
                    >
                      <span>{refreshLabel(d.refreshIntervalSeconds)}</span>
                      <span className="simsm-dashboards-admin__cell-meta">
                        <span className="simsm-dashboards-admin__cell-meta-key">
                          {t('dashboards_admin.meta.default_range')}
                        </span>{' '}
                        {t(`dashboards_admin.default_range.${d.defaultRange}`)}
                        {' · '}
                        <span className="simsm-dashboards-admin__cell-meta-key">
                          {t('dashboards_admin.meta.max_range')}
                        </span>{' '}
                        {t(`dashboards_admin.max_range.${d.maxRange}`)}
                        {' · '}
                        <span className="simsm-dashboards-admin__cell-meta-key">
                          {t('dashboards_admin.meta.max_lookback')}
                        </span>{' '}
                        {t(`dashboards_admin.max_range.${d.maxLookback || d.maxRange}`)}
                      </span>
                    </span>,
                    <span
                      key={`version-${d.id}`}
                      slot={`cell-version-${rowIndex}`}
                      className="simsm-dashboards-admin__cell simsm-dashboards-admin__cell--stack simsm-dashboards-admin__cell--version"
                    >
                      <span className="simsm-dashboards-admin__cell-version">v{d.version}</span>
                      <span className="simsm-dashboards-admin__cell-meta">
                        <span className="simsm-dashboards-admin__cell-meta-key">
                          {t('dashboards_admin.meta.published_at')}
                        </span>{' '}
                        {formatDateTime(d.publishedAtIso)}
                      </span>
                    </span>,
                    <span
                      key={`actions-${d.id}`}
                      slot={`cell-actions-${rowIndex}`}
                      className="simsm-dashboards-admin__cell-actions"
                    >
                      {/* `strict` + `text`, as the wireframe draws all three
                          (AdminTablouri.tsx:109-111 `btn text strict sm`).
                          Filled neutral buttons made the cluster 60px wider
                          than the register had room for, which is how the
                          reorder arrows and the edit action ended up outside
                          the frame. */}
                      <MudButton
                        shape="circular"
                        variant="strict"
                        appearance="text"
                        size="sm"
                        iconOnly
                        label={t('dashboards_admin.action.move_up')}
                        disabled={index === 0 || hasFilters || reorder.isPending}
                        onClick={() => moveDashboard(index, -1)}
                      >
                        <MudIcon slot="icon" name="arrow-up" size={16} />
                      </MudButton>
                      <MudButton
                        shape="circular"
                        variant="strict"
                        appearance="text"
                        size="sm"
                        iconOnly
                        label={t('dashboards_admin.action.move_down')}
                        disabled={index === rows.length - 1 || hasFilters || reorder.isPending}
                        onClick={() => moveDashboard(index, 1)}
                      >
                        <MudIcon slot="icon" name="arrow-down" size={16} />
                      </MudButton>
                      <MudButton
                        shape="circular"
                        variant="strict"
                        appearance="text"
                        size="sm"
                        onClick={() => navigate(`/system-admin/dashboards-admin/${d.slug}/edit`)}
                      >
                        {t('dashboards_admin.action.edit')}
                      </MudButton>
                      <RowActionsMenu
                        label={t('dashboards_admin.action.more')}
                        actions={[
                          {
                            label: t('dashboards_admin.action.view'),
                            onSelect: () => navigate(`/system-admin/dashboards/${d.slug}`),
                          },
                          {
                            label: t('dashboards_admin.action.versions'),
                            onSelect: () => setVersionsFor(d),
                          },
                          {
                            label: t('dashboards_admin.action.export'),
                            onSelect: () =>
                              window.open(`/api/v1/admin/dashboards/${d.id}/export`, '_blank'),
                          },
                          ...(uid
                            ? [
                                {
                                  label: t('dashboards_admin.action.open_grafana'),
                                  onSelect: () => openInEditor(uid),
                                },
                              ]
                            : []),
                          {
                            label: d.active
                              ? t('dashboards_admin.action.deactivate')
                              : t('dashboards_admin.action.activate'),
                            onSelect: async () => {
                              if (d.active) {
                                const ok = await confirm({
                                  title: t('confirm.deactivate.title'),
                                  body: t('confirm.dashboard.body', { name: d.title }),
                                  confirmLabel: t('confirm.action.deactivate'),
                                  destructive: true,
                                });
                                if (!ok) return;
                              }
                              toggleActive.mutate({ id: d.id, active: !d.active });
                            },
                            disabled: toggleActive.isPending,
                            danger: d.active,
                          },
                        ]}
                      />
                    </span>,
                  ];
                })}
              </MudTable>
            </div>
          </QueryState>

          {/* The authoring half of the owner's 2026-09-01 split, beside the sharing above rather
              than behind a second entry: Grafana with its own chrome — Save, Add panel, the panel
              editor — because this is where the dashboard is built. The frame is the same proxy
              path the reading page uses; what differs is the absence of `kiosk`, and
              features/dashboards/grafana-embed.ts states plainly that this is a presentation and
              not a permission. */}
          <section className="simsm-dashboards-admin__editor" ref={editorRef}>
            <header className="simsm-dashboards-admin__editor-header">
              <div className="simsm-dashboards-admin__editor-heading">
                <h2 className="simsm-dashboards-admin__editor-title">
                  {t('dashboards_admin.editor.title')}
                </h2>
                <p className="simsm-dashboards-admin__form-hint">
                  {t('dashboards_admin.editor.lede')}
                </p>
              </div>
              <div className="simsm-dashboards-admin__editor-actions">
                <MudButton
                  shape="circular"
                  variant="neutral"
                  size="sm"
                  disabled={editorTarget.kind === 'browse'}
                  onClick={() => setEditorTarget({ kind: 'browse' })}
                >
                  {t('dashboards_admin.editor.browse')}
                </MudButton>
                <MudButton
                  shape="circular"
                  variant="neutral"
                  size="sm"
                  onClick={() => setEditorTarget({ kind: 'new' })}
                >
                  <MudIcon slot="icon-start" name="plus-large" size={16} />
                  {t('dashboards_admin.editor.new')}
                </MudButton>
              </div>
            </header>
            {editorTarget.kind === 'dashboard' ? (
              <p className="simsm-dashboards-admin__editor-subject">
                {t('dashboards_admin.editor.editing', { title: sourceTitle(editorTarget.uid) })}
              </p>
            ) : null}
            {/* Lazy: Grafana's own surface is a heavy page, and the administrator who came here to
                read the sharing table should not pay for it before scrolling to the editor. The row
                action scrolls, which is what loads it. */}
            <iframe
              key={grafanaEditorSrc(editorTarget)}
              title={t('dashboards_admin.editor.frame_title')}
              src={grafanaEditorSrc(editorTarget)}
              className="simsm-dashboards-admin__editor-frame"
              loading="lazy"
            />
          </section>
        </>
      )}

      <MudModal
        closeLabel={t('common.close')}
        open={versionsFor !== null}
        size="md"
        titleText={
          versionsFor ? t('dashboards_admin.versions.title', { title: versionsFor.title }) : ''
        }
        // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 — applied to
        // every modal uniformly; a no-op here until a descendant emits its own mudClose.
        onMudClose={onModalDismiss(() => {
          setVersionsFor(null);
          setRestoreTarget(null);
        })}
      >
        {versionsFor ? (
          <div className="simsm-dashboards-admin__versions">
            {versionRows.length === 0 ? (
              <MudInfoBox emphasis="strong" role="status" variant="info">
                {t('dashboards_admin.versions.empty')}
              </MudInfoBox>
            ) : (
              <div className="simsm-dashboards-admin__table">
                <MudTable
                  className="simsm-table"
                  columns={versionColumns}
                  rows={versionTableRows}
                  rowStyle="divided"
                  disableSort={true}
                  aria-label={t('dashboards_admin.versions.title', { title: versionsFor.title })}
                >
                  <span slot="header-cell-actions" className="visually-hidden">
                    {t('dashboards_admin.column.actions')}
                  </span>
                  {versionRows.map((row, rowIndex) => [
                    <span
                      key={`version-${row.version}`}
                      slot={`cell-version-${rowIndex}`}
                      className="simsm-dashboards-admin__cell-version"
                    >
                      v{row.version}
                    </span>,
                    <span
                      key={`when-${row.version}`}
                      slot={`cell-when-${rowIndex}`}
                      className="simsm-dashboards-admin__cell simsm-dashboards-admin__cell--nowrap"
                    >
                      {formatDateTime(row.createdAtIso)}
                    </span>,
                    <span
                      key={`by-${row.version}`}
                      slot={`cell-by-${rowIndex}`}
                      className="simsm-dashboards-admin__cell simsm-dashboards-admin__cell--nowrap"
                    >
                      {row.createdBy || (
                        <span className="simsm-dashboards-admin__cell-muted">·</span>
                      )}
                    </span>,
                    <span
                      key={`actions-${row.version}`}
                      slot={`cell-actions-${rowIndex}`}
                      className="simsm-dashboards-admin__cell-actions"
                    >
                      {/* The version in force is the list's first row (UC-DASH-08); restoring
                          it would restore nothing, and no snapshot row holds it. */}
                      {row.version === versionsFor.version ? (
                        <MudTag
                          variant="status"
                          semantic="success"
                          label={t('dashboards_admin.versions.current')}
                        />
                      ) : restoreTarget === row.version ? (
                        <>
                          <MudButton
                            shape="circular"
                            variant="primary"
                            size="sm"
                            disabled={restore.isPending}
                            onClick={() =>
                              restore.mutate({ id: versionsFor.id, version: row.version })
                            }
                          >
                            {t('dashboards_admin.versions.confirm')}
                          </MudButton>
                          <MudButton
                            shape="circular"
                            variant="neutral"
                            size="sm"
                            onClick={() => setRestoreTarget(null)}
                          >
                            {t('dashboards_admin.action.cancel')}
                          </MudButton>
                        </>
                      ) : (
                        <MudButton
                          shape="circular"
                          variant="neutral"
                          size="sm"
                          onClick={() => setRestoreTarget(row.version)}
                        >
                          {t('dashboards_admin.versions.restore')}
                        </MudButton>
                      )}
                    </span>,
                  ])}
                </MudTable>
              </div>
            )}
          </div>
        ) : null}
        <div slot="actions" className="simsm-dashboards-admin__modal-actions">
          <MudButton
            shape="circular"
            variant="primary"
            size="md"
            onClick={() => {
              setVersionsFor(null);
              setRestoreTarget(null);
            }}
          >
            {t('dashboards_admin.versions.close')}
          </MudButton>
        </div>
      </MudModal>

      <MudModal
        closeLabel={t('common.close')}
        open={showImport}
        size="md"
        titleText={t('dashboards_admin.import.title')}
        // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 — applied to
        // every modal uniformly; a no-op here until a descendant emits its own mudClose.
        onMudClose={onModalDismiss(() => setShowImport(false))}
      >
        <div className="simsm-dashboards-admin__import">
          <p className="simsm-dashboards-admin__form-hint">{t('dashboards_admin.import.lede')}</p>
          <MudFileInput
            accept={DASHBOARD_IMPORT_ACCEPT}
            onMudChange={(e: CustomEvent<{ files: File[] }>) =>
              setImportFile(e.detail.files[0] ?? null)
            }
          />
          {/* MUD 1.2 has no success info-box, so a clean import stays a toast held at
              the column's width by `.simsm-inline-outcome`; one with errors is a warning box. */}
          {importResult && importResult.errors.length > 0 ? (
            <MudInfoBox emphasis="strong" role="alert" variant="warning">
              {t('dashboards_admin.import.result', {
                imported: importResult.imported,
                errors: importResult.errors.length,
              })}
            </MudInfoBox>
          ) : importResult ? (
            <div className="simsm-inline-outcome">
              <MudToast closable={false} variant="success">
                {t('dashboards_admin.import.result', {
                  imported: importResult.imported,
                  errors: importResult.errors.length,
                })}
              </MudToast>
            </div>
          ) : null}
          {importResult?.warnings && importResult.warnings.length > 0 ? (
            <MudInfoBox emphasis="strong" role="alert" variant="warning">
              {t('dashboards_admin.import.grafana_differs', {
                slugs: importResult.warnings.map((w) => w.slug).join(', '),
              })}
            </MudInfoBox>
          ) : null}
        </div>
        <div slot="actions" className="simsm-dashboards-admin__modal-actions">
          <MudButton
            shape="circular"
            variant="neutral"
            size="md"
            onClick={() => setShowImport(false)}
          >
            {t('dashboards_admin.action.cancel')}
          </MudButton>
          <MudButton
            shape="circular"
            variant="primary"
            size="md"
            disabled={!importFile || doImport.isPending}
            onClick={() => importFile && doImport.mutate(importFile)}
          >
            {t('dashboards_admin.import.submit')}
          </MudButton>
        </div>
      </MudModal>
    </section>
  );
}
